<template>
  <div class="product-information mt-8 lg:mt-16 mb-8 lg:mb-10 flex flex-col gap-8 lg:gap-10">
    <LazyVisibleAnchorNavigationSectionWrapper
      v-if="siblingCategories.length"
      :id="PDP_NAV_ANCHORS.SIBLING_CATEGORIES"
      class="flex flex-col gap-4"
      hydrate-on-visible
    >
      <ProductAnchorTitle
        :title="t('Similar Categories')"
      />
      <PdpSiblingCategories
        :categories="siblingCategories"
      />
    </LazyVisibleAnchorNavigationSectionWrapper>
    <LazyVisibleAnchorNavigationSectionWrapper
        v-if="productDataLoaded"
        :id="PDP_NAV_ANCHORS.TECHNICAL_DETAILS"
        class="flex flex-col gap-4"
        hydrate-on-visible
    >
      <ProductAnchorTitle
        :title="t('Technical details')"
      />
      <ComparisonProducts
        v-if="alternativeProductsData.length"
        :current-product="product"
        :current-attributes-localized="attributesLocalized"
        :comparison-products-data="alternativeProductsData"
      />
      <TechnicalDetails v-else />
      <DeliveryPackage :delivery-package="deliveryPackage" />
    </LazyVisibleAnchorNavigationSectionWrapper>
    <AlternativeProducts
      v-if="isAlternativeProductsModalOpen && alternativeProductsData.length"
      :products-data="alternativeProductsData"
      :current-sku="productSku"
      @close="closeAlternativeProductsModal"
    />
    <LazyVisibleAnchorNavigationSectionWrapper
      v-if="!isProductDescriptionEmpty"
      :id="PDP_NAV_ANCHORS.PRODUCT_DESCRIPTION"
      class="flex flex-col gap-4"
      hydrate-on-visible
    >
      <ProductAnchorTitle
        :title="t('Product Description')"
      />
      <ProductDescription
        :product-description="productDescription"
      />
    </LazyVisibleAnchorNavigationSectionWrapper>
    <AnchorNavigationSectionWrapper
      v-if="shouldShowRelatedAccessories"
      :id="PDP_NAV_ANCHORS.RELATED_ACCESSORIES"
      ref="relatedAccessoriesNode"
      class="flex flex-col gap-4"
    >
      <ProductSliderContainer
        :products="relatedAccessories"
        :loading="relatedAccessoriesLoading"
        :title="t('Accessories')"
        :size="PRODUCT_CARD_SIZE.MEDIUM"
        @product-clicked="accessoryClicked"
      />
    </AnchorNavigationSectionWrapper>
    <ProductSliderContainerSkeleton v-if="!shouldShowRelatedAccessories && relatedAccessoriesLoading" />
    <AnchorNavigationSectionWrapper
        v-if="shouldShowParentsForAccessory"
        :id="PDP_NAV_ANCHORS.PARENTS_FOR_ACCESSORY"
        ref="relatedParentProductsNode"
        class="flex flex-col gap-4"
    >
      <ProductSliderContainer
        :products="parentsForAccessory"
        :loading="parentsForAccessoryLoading"
        :title="t('Matches to')"
        :size="PRODUCT_CARD_SIZE.MEDIUM"
        @product-clicked="parentClicked"
      />
    </AnchorNavigationSectionWrapper>
    <ProductSliderContainerSkeleton v-if="!shouldShowParentsForAccessory && parentsForAccessoryLoading" />
    <LazyVisibleAnchorNavigationSectionWrapper
      v-if="shouldShowSimilarProducts"
      class="relative"
      hydrate-on-visible
    >
      <SimilarProducts
        class="lg:hidden max-lg:wide-bg-EXPONDO-secondary-light py-2"
        :similar-products="similarProducts"
        :similar-products-loading="similarProductsLoading"
        :source-sku="product.sku ?? ''"
        :has-arrows="false"
        :size="PRODUCT_CARD_SIZE.TILE_MINI"
      />
      <SimilarProducts
        class="hidden lg:flex"
        :similar-products="similarProducts"
        :similar-products-loading="similarProductsLoading"
        :source-sku="product.sku ?? ''"
      />
    </LazyVisibleAnchorNavigationSectionWrapper>
    <ClientOnly>
      <AnchorNavigationSectionWrapper>
        <LastSeenProducts />
      </AnchorNavigationSectionWrapper>
    </ClientOnly>
    <LazyVisibleAnchorNavigationSectionWrapper
      v-if="!isAboutBrandEmpty"
      :id="PDP_NAV_ANCHORS.ABOUT_BRAND"
      class="flex flex-col gap-4"
      hydrate-on-visible
    >
      <ProductAnchorTitle
        :title="t('About the brand')"
      /><AboutTheBrand :brand="aboutBrand" />
    </LazyVisibleAnchorNavigationSectionWrapper>
  </div>
</template>

<script setup lang="ts">
import isEmpty from 'lodash/isEmpty.js';
import type { ProductVariant } from '~~/types/commercetools';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import type { SiblingCategory } from '~~/types/product/ProductCategories';
import type { BrandInfo } from '~~/constants/brands';
import type { ProductDescription } from '~~/types/product/ProductDescription';
import { PDP_NAV_ANCHORS } from '~~/constants/pdpNavAnchors';
import type { ProductCardSize } from '~~/constants/productCardSize';
import { PRODUCT_CARD_SIZE } from '~~/constants/productCardSize';
import ProductAnchorTitle from '~/components/atoms/Product/ProductAnchorTitle/ProductAnchorTitle.vue';
import type { AlternativeProductData } from '~~/types/product/AlternativeProducts';
import type { ProductProcessedAttributes } from '~~/types/product/attribute/ProductProcessedAttributes';
import DeliveryPackage from '~/components/molecules/Product/details/DeliveryPackage.vue';
import AlternativeProducts from '~/components/molecules/Product/AlternativeProducts/AlternativeProducts.vue';
import { getProductIsOnStock } from '~~/helpers/product/getProductStockInfo/getProductStockInfo';
import LastSeenProducts from '~/components/organisms/Product/LastSeenProducts/LastSeenProducts.vue';

const { t } = useI18n();

interface Props {
  product: ProductVariantExtended;
  attributesLocalized: ProductProcessedAttributes;
  productDescription: ProductDescription;
  aboutBrand: BrandInfo;
  relatedAccessories: ProductVariant[];
  relatedAccessoriesLoading: boolean;
  parentsForAccessory: ProductVariant[];
  parentsForAccessoryLoading: boolean;
  shouldShowRelatedAccessories: boolean;
  shouldShowParentsForAccessory: boolean;
  size?: ProductCardSize;
  siblingCategories: SiblingCategory[];
  alternativeProductsData: AlternativeProductData[];
  similarProducts: ProductVariantExtended[];
  similarProductsLoading: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: PRODUCT_CARD_SIZE.RESPONSIVE,
});

const route = useRoute();

const isProductDescriptionEmpty = computed(() => isEmpty(props.productDescription));
const isAboutBrandEmpty = computed(() => isEmpty(props.aboutBrand));
const slug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug;
const { loaded: productDataLoaded } = useProductExtended(slug);

const productSku = computed(() => props.product?.sku ?? '');

const relatedAccessoriesNode = ref<HTMLElement | null>(null);
const relatedParentProductsNode = ref<HTMLElement | null>(null);

const deliveryPackage = computed(() => props.attributesLocalized?.deliveryPackage || []);

const accessoryClicked = (_accessorySku: string) => {};

const parentClicked = (_parentSku: string) => {};

const shouldShowSimilarProducts = computed(() => {

  return props.similarProducts.length > 0 && !props.similarProductsLoading;

});

const {
  openAlternativeProductsModal,
  isAlternativeProductsModalOpen,
  closeAlternativeProductsModal,
} = useAlternativeProductsModalStore();

const isProductOnStock = computed(() => getProductIsOnStock(props.product));

watch(isProductOnStock, (inStock) => {

  if (!inStock) {

    openAlternativeProductsModal();

  } else {

    closeAlternativeProductsModal();

  }

}, { immediate: true });
</script>
